import {
  Alert,
  Chip,
  IconButton,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { useCallback, useMemo, useState } from "react";
import { useStableQuery } from "../hooks/useStableQuery";
import { useAllPages } from "../hooks/useAllPages";
import { api } from "../../../convex/_generated/api";
import { useCycleFilter } from "../cycleFilterContext";
import { QbPostedBadge } from "../components/qb/QbPostedBadge";
import { HowToFindModal } from "../components/qb/HowToFindModal";
import type { QbAwareTransactionRow } from "../types/qb-aware";
import { DataTableShell } from "../components/table/DataTableShell";
import { CountingFilterSelect } from "../components/table/CountingFilterSelect";
import { UploadInvoiceButton } from "../components/review/UploadInvoiceButton";
import { useActorEmail } from "../hooks/useActorEmail";

function formatCurrency(amount: number) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(amount);
}

function formatDate(isoDate: string) {
  return new Date(isoDate).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function daysUnmatched(dateStr: string): number {
  const txDate = new Date(dateStr);
  const now = new Date();
  return Math.floor((now.getTime() - txDate.getTime()) / (1000 * 60 * 60 * 24));
}

function daysColor(days: number): "default" | "warning" | "error" {
  if (days > 30) return "error";
  if (days > 7) return "warning";
  return "default";
}

type AgeBucket = "all" | "fresh" | "warning" | "stale";

export function InvoiceGaps() {
  const [searchTerm, setSearchTerm] = useState("");
  const [ageFilter, setAgeFilter] = useState<AgeBucket>("all");
  const [howToTarget, setHowToTarget] = useState<QbAwareTransactionRow | null>(
    null,
  );
  const { cycleArg } = useCycleFilter();
  const actorEmail = useActorEmail();
  const [snack, setSnack] = useState<{ message: string; severity: "success" | "error" } | null>(
    null,
  );
  const showSnack = useCallback(
    (message: string, severity: "success" | "error") => setSnack({ message, severity }),
    [],
  );

  // Client request 2026-10-16: every unmatched transaction, not the first 500.
  const { results: pagedRows, loading: pageLoading } = useAllPages(
    api.transactions.listPaginatedWithQb,
    { ...cycleArg, matchStatus: "unmatched" as const },
  );
  const data = pageLoading ? undefined : pagedRows;
  const loading = data === undefined;
  const allRows: any[] = data ?? [];

  // Compute buckets on the unfiltered data so the dropdown shows true counts
  const ageBucketCounts = useMemo(() => {
    const counts: Record<AgeBucket, number> = { all: 0, fresh: 0, warning: 0, stale: 0 };
    for (const r of allRows) {
      const d = daysUnmatched(r.date);
      counts.all++;
      if (d <= 7) counts.fresh++;
      else if (d <= 30) counts.warning++;
      else counts.stale++;
    }
    return counts;
  }, [allRows]);

  const filtered = useMemo(() => {
    return allRows.filter((r: any) => {
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        if (
          !(r.description?.toLowerCase() ?? "").includes(term) &&
          !(r.vendorName?.toLowerCase() ?? "").includes(term)
        ) {
          return false;
        }
      }
      if (ageFilter !== "all") {
        const d = daysUnmatched(r.date);
        if (ageFilter === "fresh" && d > 7) return false;
        if (ageFilter === "warning" && (d <= 7 || d > 30)) return false;
        if (ageFilter === "stale" && d <= 30) return false;
      }
      return true;
    });
  }, [allRows, searchTerm, ageFilter]);

  const rows = filtered.map((r: any) => ({ ...r, id: r._id }));

  const columns: GridColDef[] = [
    {
      field: "date",
      headerName: "Date",
      width: 130,
      sortable: true,
      renderCell: (p: GridRenderCellParams) => formatDate(p.value),
    },
    {
      field: "description",
      headerName: "Description",
      flex: 2,
      minWidth: 240,
      sortable: true,
    },
    {
      field: "vendorName",
      headerName: "Vendor",
      width: 160,
      sortable: true,
      renderCell: (p: GridRenderCellParams) => (p.value as string) ?? "—",
    },
    {
      field: "amount",
      headerName: "Amount",
      width: 130,
      sortable: true,
      align: "right",
      headerAlign: "right",
      renderCell: (p: GridRenderCellParams) => (
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {formatCurrency(p.value as number)}
        </Typography>
      ),
    },
    {
      field: "qbWhere",
      headerName: "Where in QB",
      width: 190,
      sortable: false,
      renderCell: (p: GridRenderCellParams) => {
        const row = p.row as any;
        return (
          <Stack direction="row" spacing={0.5} alignItems="center">
            <QbPostedBadge
              qbTransactionId={row.qbTransactionId}
              qb={row.qb}
              txDate={row.date}
              drift={row.qbDrift}
            />
            {row.qb && (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setHowToTarget(row);
                }}
                title="How to find in QuickBooks"
              >
                <HelpOutlineIcon fontSize="small" />
              </IconButton>
            )}
          </Stack>
        );
      },
    },
    {
      field: "daysUnmatched",
      headerName: "Days Unmatched",
      width: 150,
      sortable: true,
      valueGetter: (_value, row) => daysUnmatched((row as any).date),
      renderCell: (p: GridRenderCellParams) => {
        const days = p.value as number;
        return <Chip size="small" label={`${days}d`} color={daysColor(days)} />;
      },
    },
    {
      field: "uploadInvoice",
      headerName: "Invoice",
      width: 170,
      sortable: false,
      filterable: false,
      renderCell: (p: GridRenderCellParams) => (
        <UploadInvoiceButton
          transactionId={String((p.row as any)._id)}
          actorEmail={actorEmail}
          onResult={showSnack}
        />
      ),
    },
  ];

  const handleReset = () => {
    setSearchTerm("");
    setAgeFilter("all");
  };

  return (
    <>
      <DataTableShell
        title="Invoice Gap Detector"
        subtitle="Identify transactions without matching invoices for reconciliation follow-up"
        data-tour="page-title-gaps"
        rows={rows}
        columns={columns}
        loading={loading}
        totalBeforeFilters={allRows.length}
        itemLabel="unmatched transaction"
        emptyTitle="All transactions have matching invoices"
        emptyDescription="Great work — nothing to follow up on right now."
        emptyIcon={
          <ReceiptLongIcon
            sx={{ fontSize: 32, color: "var(--c-link-accent)" }}
          />
        }
        onResetFilters={handleReset}
        gridProps={{
          initialState: {
            sorting: { sortModel: [{ field: "daysUnmatched", sort: "desc" }] },
            pagination: { paginationModel: { pageSize: 25 } },
          },
        }}
        filterBar={
          <>
            <TextField
              size="small"
              label="Search"
              placeholder="Description or vendor..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ minWidth: 260 }}
            />
            <CountingFilterSelect
              label="Age"
              value={ageFilter}
              onChange={(v) => setAgeFilter(v as AgeBucket)}
              allLabel="All ages"
              minWidth={180}
              options={[
                {
                  value: "fresh",
                  label: "Fresh (≤7 days)",
                  count: ageBucketCounts.fresh,
                },
                {
                  value: "warning",
                  label: "Warning (8–30 days)",
                  count: ageBucketCounts.warning,
                },
                {
                  value: "stale",
                  label: "Stale (>30 days)",
                  count: ageBucketCounts.stale,
                },
              ]}
            />
          </>
        }
      />

      {howToTarget && (
        <HowToFindModal
          open={Boolean(howToTarget)}
          onClose={() => setHowToTarget(null)}
          txDate={howToTarget.date}
          qb={howToTarget.qb}
        />
      )}

      <Snackbar
        open={snack !== null}
        autoHideDuration={snack?.severity === "error" ? 8000 : 4000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snack?.severity ?? "success"}
          variant="filled"
          onClose={() => setSnack(null)}
        >
          {snack?.message}
        </Alert>
      </Snackbar>
    </>
  );
}
